import React, { useState, useEffect } from "react";
import Box from "@material-ui/core/Box";
import FormHelperText from "@material-ui/core/FormHelperText";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import throttle from "lodash/throttle";
import { Field, ErrorMessage } from "formik";
import { Product } from "../../model/product";

interface Props {
  name: string;
  onSearch: (input: string) => void;
  onSelect: (productNumber: string) => void;
  onReset: () => void;
  products?: Product[];
  loading: boolean;
  error?: string;
}

export const ProductSearch = ({
  name,
  onSelect,
  onSearch,
  onReset,
  products,
  loading,
  error: searchError,
}: Props): JSX.Element => {
  const [inputValue, setInputValue] = React.useState("");
  const [options, setOptions] = useState<Product[]>(products || []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const doSearch = React.useMemo(
    () =>
      throttle((request: string) => {
        onSearch(request);
      }, 400),
    [onSearch],
  );

  useEffect(() => {
    if (inputValue === "" || inputValue.length < 3) {
      setOptions([]);
      return undefined;
    }
    doSearch(inputValue);
  }, [inputValue, doSearch]);

  useEffect(() => {
    if (products?.length) {
      setOptions(products);
    }
    return () => {
      setOptions([]);
    };
  }, [products]);

  return (
    <Box>
      <Field name={name}>
        {({
          field: { name, value, onChange },
          form: { touched, errors, setFieldValue, resetForm }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
          meta: { error },
        }: any) => (
          <Autocomplete
            multiple={false}
            autoComplete
            options={options}
            loading={loading}
            filterOptions={(x) => x}
            getOptionLabel={(p: Product) => (p ? `${p.id} ${p.name}` : "")}
            noOptionsText={inputValue.length < 3 ? "Anna vähintään 3 merkkiä" : "Tuotetta ei löydy"}
            onChange={(_: any, value: Product | null) => {
              setFieldValue(name, value);
              if (value) {
                onSelect(value.id);
              } else {
                onReset();
              }
            }}
            renderInput={(params: any) => (
              <TextField
                {...params}
                required
                label="Tuotteen tunnus tai nimi"
                margin="none"
                variant="outlined"
                error={!!error}
                onChange={handleChange}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        )}
      </Field>
      <ErrorMessage name={name}>
        {(msg) => (
          <FormHelperText error required variant="outlined">
            {msg}
          </FormHelperText>
        )}
      </ErrorMessage>
      {searchError && (
        <FormHelperText error required variant="outlined">
          {searchError}
        </FormHelperText>
      )}
    </Box>
  );
};
